const DEFAULT_RETURN_PATH = '/';

/** Any origin works: it is discarded, and only used to prove the value cannot escape to another host. */
const RESOLUTION_BASE = 'https://portal.invalid';

/** A leading slash followed by another separator or by whitespace. */
const LEADING_SEPARATOR = /^\/[\s/\\]/;

/**
 * Reads the path recorded by the route guard when it bounced an unauthenticated visitor, so a deep link
 * survives the trip through the login screen.
 *
 * Router state is untyped and reachable through history, so the value is resolved against a fixed origin
 * and rejected unless it stays on it. A prefix check alone is not enough: React Router 6 carries an
 * unpatched open-redirect advisory (GHSA-wrjc-x8rr-h8h6) precisely because `/\host` and `/%09//host`
 * look relative and are parsed as absolute.
 */
export function readReturnPath(state: unknown): string {
  if (typeof state !== 'object' || state === null || !('from' in state)) {
    return DEFAULT_RETURN_PATH;
  }

  const { from } = state;
  if (typeof from !== 'string' || !from.startsWith('/')) {
    return DEFAULT_RETURN_PATH;
  }

  try {
    const resolved = new URL(from, RESOLUTION_BASE);
    if (resolved.origin !== RESOLUTION_BASE) {
      return DEFAULT_RETURN_PATH;
    }

    const path = `${resolved.pathname}${resolved.search}${resolved.hash}`;

    // Same origin is the property that matters, but a path whose first character after the leading
    // slash is a separator or whitespace — encoded or not — has no legitimate use here and is the raw
    // material every variant of this bypass is built from.
    return LEADING_SEPARATOR.test(path) || LEADING_SEPARATOR.test(decodeURIComponent(path))
      ? DEFAULT_RETURN_PATH
      : path;
  } catch {
    return DEFAULT_RETURN_PATH;
  }
}
